import React from "react";
import { BlogContent } from "@/.storyblok/types/319751/storyblok-components";
import { storyblokEditable, SbBlokData } from "@storyblok/react";
import { renderRichText } from "@/lib/richText";
import VideoContent from "@/components/VideoContent";
import BlockquoteAuthor from "@/components/BlockquoteAuthor";

interface BlogContentProps {
  blok?: BlogContent & SbBlokData;
}

const BlogContentComponent: React.FC<BlogContentProps> = ({ blok }) => {
  if (!blok) return null;

  const contentArray = Array.isArray(blok) ? blok : [blok];

  const getBgClass = () => contentArray[0]?.bg_style === "dark" ? "bg-light" : "";

  const modifyHtmlContent = (htmlContent: string, originalContent: any) => {
    let modifiedHtml = htmlContent;

    // Handle tables
    modifiedHtml = modifiedHtml
      .replace(
        /<table>/g,
        "<div class=\"table-responsive mb-4\"><table class=\"table\">"
      )
      .replace(
        /<\/table>/g,
        "</table></div>"
      );

    // Handle table headers
    modifiedHtml = modifiedHtml.replace(
      /(<table class="table(?:\s[^"]*)?">)\s*<tbody>\s*(<tr>[\s\S]*?<\/tr>)/,
      (match, tableOpen, firstRow) => {
        if (firstRow.includes("<th>")) {
          return `${tableOpen}<thead class="table-dark">${firstRow}</thead><tbody>`;
        }
        return match;
      }
    );

    // Handle paragraphs with images
    modifiedHtml = modifiedHtml.replace(
      /<p[^>]*>(?:\s*<span[^>]*>)?(.*?)(?:<\/span>\s*)?<\/p>/g,
      (match, content) => {
        if (content.includes('<img')) {
          const imgMatches = content.match(/<img[^>]*src="([^"]+)"[^>]*>/g);
          if (!imgMatches) return match;

          let alignmentClass = '';
          if (originalContent?.content) {
            const findImageWithContent = (items: any[]): any => {
              for (const item of items) {
                if (item.type === 'paragraph' && item.content) {
                  const imageItem = item.content.find((contentItem: any) =>
                    contentItem.type === 'image' &&
                    imgMatches.some((img: string) => img.includes(contentItem.attrs?.src))
                  );
                  if (imageItem?.marks) {
                    const styledMark = imageItem.marks.find((mark: any) =>
                      mark.type === 'styled' && mark.attrs?.class
                    );
                    if (styledMark?.attrs?.class) {
                      return styledMark.attrs.class;
                    }
                  }
                }
                if (item.content) {
                  const found = findImageWithContent(item.content);
                  if (found) return found;
                }
              }
              return '';
            };

            alignmentClass = findImageWithContent(originalContent.content);
          }

          const cleanContent = content.replace(/<\/?span[^>]*>/g, '');
          return `<p${alignmentClass ? ` class="${alignmentClass}"` : ''}>${cleanContent}</p>`;
        }
        return match;
      }
    );

    // Process text color spans
    modifiedHtml = modifiedHtml.replace(
      /<span style="color: ([^"]*)">/g,
      (match, color) => {
        const colorClass = color ? `text-${color.trim()}` : '';
        return `<span class="${colorClass}">`;
      }
    );

    // Process underlined text
    modifiedHtml = modifiedHtml.replace(
      /<u>/g,
      '<span class="text-decoration-underline">'
    ).replace(
      /<\/u>/g,
      '</span>'
    );

    return modifiedHtml;
  };

  const renderContent = (content: any) => {
    if (!content) return null;

    const hasSpecialContent = content.content?.some((item: any) =>
      (item.type === "blok" &&
        item.attrs?.body?.some((block: any) =>
          block.component === "video_content" ||
          block.component === "blockquote_author"
        )
      )
    );

    if (!hasSpecialContent) {
      const rendered = renderRichText(content);
      const modified = modifyHtmlContent(rendered, content);
      return <div dangerouslySetInnerHTML={{ __html: modified }} />;
    }

    return (
      <>
        {content.content.map((item: any, index: number) => {
          if (item.type === "blok" && item.attrs?.body) {
            return item.attrs.body.map((block: any) => {
              if (block.component === "video_content") {
                return <VideoContent key={block._uid} blok={block} />;
              }
              if (block.component === "blockquote_author") {
                return <BlockquoteAuthor key={block._uid} blok={block} />;
              }
              return null;
            });
          }
          if (item.type === "paragraph") {
            const rendered = renderRichText({ type: "doc", content: [item] });
            const modified = modifyHtmlContent(rendered, { type: "doc", content: [item] });
            return (
              <div
                key={index}
                dangerouslySetInnerHTML={{ __html: modified }}
              />
            );
          }
          return null;
        })}
      </>
    );
  };

  const isPolicy = contentArray[0]?.type === "policy";

  if (isPolicy) {
    return (
      <>
        <div {...storyblokEditable(blok)} className={getBgClass()}>
          <div className="container py-4">
            <div className="row justify-content-center">
              <div className="col-lg-20 col-xl-18">
                {contentArray.map((section: any) => (
                  <React.Fragment key={section._uid}>
                    <h2 className="text-center">{section.lead}</h2>
                    {renderContent(section.content)}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        </div>
        {blok.use_bottom_line && <hr />}
      </>
    );
  }

  if (contentArray[0]?.type === "panel") {
    return (
      <div {...storyblokEditable(blok)} className={getBgClass()}>
        <div className="container py-4">
          <div className="two-thirds-centered">
            <div className="panel">
              {contentArray.map((section: any) => (
                <React.Fragment key={section._uid}>
                  <div className="text-start">
                    <h2
                      className="text-secondary mb-4"
                      id={section.lead?.replace(/\s+/g, "-").toLowerCase()}
                    >
                      {section.lead}
                    </h2>
                  </div>
                  {section.content && (
                    <div dangerouslySetInnerHTML={{
                      __html: modifyHtmlContent(renderRichText(section.content), section.content)
                    }} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div {...storyblokEditable(blok)} className={getBgClass()}>
        <div className="container blog_content py-4">
          <div className="two-thirds-centered">
            {contentArray.map((section: any) => (
              <React.Fragment key={section._uid}>
                <h2
                  className="mb-4"
                  id={section.lead.replace(/\s+/g, "-").toLowerCase()}
                >
                  {section.lead}
                </h2>
                {section.content && renderContent(section.content)}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
      {blok.use_bottom_line && <hr />}
    </>
  );
};

export default BlogContentComponent;